import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2, AlertTriangle } from "lucide-react";
import { photoUrl } from "../../lib/photos";
import { BRAND, errMsg } from "./constants";

const COND = { good: ["Good", BRAND.green], needs_repair: ["Needs repair", BRAND.orange], missing: ["Missing", "#DC2626"] };

function AddItem({ rooms, onAdd }) {
  const [f, setF] = useState({ name: "", room_id: "", qty: 1, condition: "good" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const cls = "rounded-xl border-2 px-2.5 py-2 text-sm w-full";
  return (
    <form data-testid="inventory-add-form" onSubmit={(e) => { e.preventDefault(); onAdd({ ...f, qty: +f.qty, room_id: f.room_id || null }).then((ok) => ok && setF({ ...f, name: "", qty: 1 })); }} className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      <input required placeholder="Item (Mattress, Fan…)" data-testid="inventory-name-input" className={`${cls} col-span-2 sm:col-span-1`} value={f.name} onChange={set("name")} />
      <select data-testid="inventory-room-select" className={cls} value={f.room_id} onChange={set("room_id")}>
        <option value="">Common area</option>
        {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
      <input type="number" min={0} data-testid="inventory-qty-input" className={cls} value={f.qty} onChange={set("qty")} />
      <select data-testid="inventory-condition-select" className={cls} value={f.condition} onChange={set("condition")}>
        {Object.entries(COND).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <button data-testid="inventory-add-submit" className="col-span-2 sm:col-span-1 rounded-full py-2 text-sm font-bold text-white" style={{ background: BRAND.green }}>Add item</button>
    </form>
  );
}

function ItemRow({ it, roomName, onPatch, onPhoto, onDelete, canDelete }) {
  return (
    <div data-testid={`inventory-row-${it.id}`} className="py-3 border-t flex flex-wrap items-center gap-3 text-sm" style={{ borderColor: BRAND.line }}>
      <div className="flex gap-1">
        {(it.photos || []).slice(0, 3).map((p) => <img key={p} src={photoUrl(p)} alt="" className="w-11 h-11 rounded-lg object-cover border" />)}
        <label data-testid={`inventory-photo-${it.id}`} className="w-11 h-11 rounded-lg border-2 border-dashed grid place-items-center cursor-pointer" style={{ borderColor: BRAND.line }}>
          <Camera className="w-4 h-4" style={{ color: BRAND.muted }} />
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files[0] && onPhoto(it, e.target.files[0])} />
        </label>
      </div>
      <div className="flex-1 min-w-[8rem]">
        <b>{it.name}</b> <span className="text-xs" style={{ color: BRAND.muted }}>· {roomName} · by {it.updated_by}</span>
      </div>
      <input type="number" min={0} data-testid={`inventory-qty-${it.id}`} defaultValue={it.qty} onBlur={(e) => +e.target.value !== it.qty && onPatch(it, { qty: +e.target.value })} className="w-16 rounded-xl border-2 px-2 py-1 text-xs" />
      <select data-testid={`inventory-cond-${it.id}`} value={it.condition} onChange={(e) => onPatch(it, { condition: e.target.value })} className="rounded-xl border-2 px-2 py-1 text-xs font-bold" style={{ color: COND[it.condition][1] }}>
        {Object.entries(COND).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
      </select>
      {canDelete && <button data-testid={`inventory-delete-${it.id}`} onClick={() => onDelete(it)} aria-label="Delete item"><Trash2 className="w-4 h-4 text-red-600" /></button>}
    </div>
  );
}

export function InventoryPanel({ pid, authFetch, API, isOwner }) {
  const [d, setD] = useState({ items: [], alerts: 0 });
  const [rooms, setRooms] = useState([]);
  const load = useCallback(() => {
    authFetch({ url: `${API}/homestay/properties/${pid}/inventory` }).then((r) => setD(r.data)).catch(() => {});
    authFetch({ url: `${API}/homestay/properties/${pid}/board` }).then((r) => setRooms(r.data.rooms)).catch(() => {});
  }, [pid, authFetch, API]);
  useEffect(() => { load(); }, [load]);

  const add = async (body) => {
    try { await authFetch({ method: "post", url: `${API}/homestay/properties/${pid}/inventory`, data: body }); toast.success("Item added"); load(); return true; }
    catch (e) { toast.error(errMsg(e, "Could not add item")); return false; }
  };
  const patch = async (it, data) => {
    try { await authFetch({ method: "patch", url: `${API}/homestay/inventory/${it.id}`, data }); load(); }
    catch (e) { toast.error(errMsg(e, "Could not update")); }
  };
  const photo = async (it, file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await authFetch({ method: "post", url: `${API}/upload/listing-photo`, data: form, headers: { "Content-Type": "multipart/form-data" } });
      await patch(it, { photos: [...(it.photos || []), r.data.path].slice(-6) });
      toast.success("Photo saved");
    } catch (e) { toast.error(errMsg(e, "Upload failed")); }
  };
  const del = async (it) => {
    if (!window.confirm(`Delete ${it.name}?`)) return;
    try { await authFetch({ method: "delete", url: `${API}/homestay/inventory/${it.id}` }); load(); }
    catch (e) { toast.error(errMsg(e, "Could not delete")); }
  };
  const roomName = (id) => rooms.find((r) => r.id === id)?.name || "Common area";

  return (
    <div data-testid="inventory-panel" className="rounded-2xl border-2 bg-white p-4 space-y-3" style={{ borderColor: BRAND.line }}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold" style={{ color: BRAND.green }}>Inventory tracking</div>
        {d.alerts > 0 && <span data-testid="inventory-alerts" className="bed-blink text-xs font-bold inline-flex items-center gap-1" style={{ color: "#DC2626" }}><AlertTriangle className="w-3.5 h-3.5" /> {d.alerts} need attention</span>}
      </div>
      <AddItem rooms={rooms} onAdd={add} />
      <div data-testid="inventory-list">
        {d.items.length === 0 && <div className="text-xs" style={{ color: BRAND.muted }}>No items yet — add mattresses, fans, lockers, keys…</div>}
        {d.items.map((it) => <ItemRow key={it.id} it={it} roomName={roomName(it.room_id)} onPatch={patch} onPhoto={photo} onDelete={del} canDelete={isOwner} />)}
      </div>
    </div>
  );
}
